/**
 * GradeBenefitContainer
 * --
 */
import React from 'react';
import GradeBenefitPresenter from './GradeBenefitPresenter';
import { useGetGrades } from 'hooks/GradeHooks';
import Logger from 'utils/logger';
import {
  productsQueryKeys,
  useGetProducts,
  useUpsertProduct,
} from 'hooks/ProductHooks';
import { menuQueryKeys, useGetMenus, useUpsertMenu } from 'hooks/MenuHooks';
import MessageAlert, { isNull } from 'utils';
import Alert from 'utils/AlertManager';
import { useQueryClient } from '@tanstack/react-query';
import { useGetAllCategories } from 'hooks/ProductCategoryHooks';
import { useGetShopInformations } from 'hooks/ShopInformationHooks';

const logger = new Logger('GradeBenefitContainer');

const GRADE_BENEFIT = {
  discount_rate: 0,
  discount_type: 'R',
  reserve_rate: 0,
  reserve_type: 'R',
};

const INITIAL_VALUES = {
  name: null,
  type: null,
  category: null,
};

const GradeBenefitContainer = () => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== Mutate ===== */
  const queryClient = useQueryClient();

  const { upsertProduct } = useUpsertProduct();
  const { upsertMenu } = useUpsertMenu();

  /* ===== VARIABLE ===== */
  const MUTATE = {
    product: upsertProduct,
    menu: upsertMenu,
  };
  const queryKey = {
    product: {
      key: '["product_file", "shop_information", "product_category"]',
      get: (query) =>
        productsQueryKeys.getProducts({
          join_tables: query,
        }),
    },

    menu: {
      key: '["menu_file", "shop_information", "product_category"]',
      get: (query) =>
        menuQueryKeys.getMenus({
          join_tables: query,
        }),
    },
  };

  /* ===== Data ===== */
  // 등급 데이터 전체 조회
  const { data: Grades } = useGetGrades(
    {
      is_enable: 'Y',
    },
    {
      select: (res) => {
        const { status, message, data } = res;

        if (!status) {
          return [];
        }

        if (status !== 200) {
          logger.warn('등급데이터호출실패', message);
          return [];
        }

        return data;
      },
      initialData: [],
    }
  );

  // 상품 데이터 전체 조회
  const { data: Products } = useGetProducts(
    {
      join_tables: queryKey.product.key,
      type: 'DELIVERY',
    },
    {
      select: (res) => {
        const { status, message, data } = res;

        if (!status) {
          return [];
        }

        if (status !== 200) {
          logger.warn('상품데이터호출실패', message);
          return [];
        }

        const newData = data.map((_item) => ({
          ..._item,
          key: _item.product_id,
          thumbnail: _item.product_file.length
            ? _item.product_file.find((file) => file.type === 'THUMBNAIL')
                ?.file_url
            : null,
        }));

        return newData;
      },
      initialData: [],
    }
  );

  // 메뉴 데이터 전체 조회
  const { data: Menus } = useGetMenus(
    {
      join_tables: queryKey.menu.key,
    },
    {
      select: (res) => {
        const { status, message, data } = res;

        if (!status) {
          return [];
        }

        if (status !== 200) {
          logger.warn('메뉴데이터호출실패', message);
          return [];
        }

        const newData = data.map((_item) => ({
          ..._item,
          key: _item.menu_id,
          thumbnail: _item.menu_file.length
            ? _item.menu_file.find((file) => file.type === 'THUMBNAIL')
                ?.file_url
            : null,
        }));

        return newData;
      },
      initialData: [],
    }
  );

  // 카테고리 전체 조회
  const { data: Categories } = useGetAllCategories(null, {
    select: (res) => {
      const { status, message, data } = res;

      if (!status) {
        return [];
      }

      if (status !== 200) {
        logger.warn('카테고리데이터호출실패', message);
        return [];
      }

      return data;
    },
    initialData: [],
  });

  // 입점업체 전체 조회
  const { data: Shops } = useGetShopInformations(null, {
    initialData: [],
    select: (res) => {
      const { status, data, message } = res;

      if (!status) {
        return [];
      }

      if (status !== 200) {
        logger.warn('메뉴데이터호출실패', message);
        return [];
      }

      const filtered = data.filter((_item) => _item.type !== 'STORE');
      return filtered;
    },
  });

  /* ===== STATE ===== */
  const [initialBenefits, setInitialBenefits] = React.useState({});
  const [benefit, setBenefit] = React.useState({});
  const [tabKey, setTabKey] = React.useState('product');
  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedItems, setSelectedItems] = React.useState([]);
  const [isOpened, setIsOpened] = React.useState(false);
  const searchInput = React.useRef(null);
  const [searchedColumn, setSearchedColumn] = React.useState(INITIAL_VALUES);

  /* ===== FUNCTION ===== */
  /**
   * 등급혜택 설정
   * --
   * @param {string | number} key
   * @param {string} target [discount_rate, discount_type, reserve_rate, reserve_type]
   * @param {string | number} value
   */
  const handleChange = (key, target, value) => {
    setBenefit((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [target]: value,
      },
    }));
  };

  /**
   * 상품 선택
   * --
   * @param {Array} keys
   */
  const handleSelect = (keys) => {
    setSelectedItems(keys);
  };

  const valueCheck = (benefit) => {
    let status = true;

    Object.values(benefit).forEach(({ discount_rate, reserve_rate }) => {
      if (
        typeof discount_rate !== 'number' ||
        typeof reserve_rate !== 'number' ||
        discount_rate < 0 ||
        reserve_rate < 0
      ) {
        status = false;
      }
    });
    return status;
  };
  /**
   * 등급헤택 저장
   * --
   * @returns
   */
  const handleSubmit = async () => {
    if (
      !Alert.confirm(
        `${selectedItems.length}개의 상품에 등급혜택을 일괄 적용하시겠습니까?`
      )
    )
      return;

    const mutate = MUTATE[tabKey];

    if (!valueCheck(benefit))
      return MessageAlert.warning('등급 및 적립은 0이상 입력해주세요.');
    try {
      setIsLoading(true);

      const arr = selectedItems.map((item) => ({
        [`${tabKey}_id`]: item[`${tabKey}_id`],
        grade_benefit_info: benefit,
      }));
      const { status, message } = await mutate(arr);

      if (status !== 200) {
        logger.warn('일괄수정 실패!', message);
        MessageAlert.warning('등급혜택 저장 실패!');
        return;
      }

      setBenefit(initialBenefits);
      setSelectedItems([]);
      queryClient.invalidateQueries({
        queryKey: queryKey[tabKey].get(queryKey[tabKey].key).queryKey,
      });
      handleToggleModal();
      MessageAlert.success('등급 혜택이 성공적으로 저장되었습니다.');
    } catch (err) {
      logger.error('handleSubmit', err);
      MessageAlert.error('등급혜택 저장 실패!');
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  /**
   * 혜택 수정 모달 핸들러
   * --
   * @param {Object} benefit
   */
  const handleToggleModal = (benefit) => {
    setBenefit(benefit || initialBenefits);
    setIsOpened(!isOpened);
  };

  /* ===== HOOKS ===== */
  /**
   * 등급 기본할인 설정
   * --
   */
  React.useEffect(() => {
    if (!Grades.length) return;

    const initial = Grades.reduce((acc, cur) => {
      acc[cur.grade_id] = GRADE_BENEFIT;

      return acc;
    }, {});

    setInitialBenefits(initial);
  }, [Grades]);

  /**
   * 탭키 변경 시 선택상품 초기화
   * --
   */
  React.useEffect(() => {
    setSelectedItems([]);
    setSearchedColumn(INITIAL_VALUES);
  }, [tabKey]);

  /* ===== Props ===== */
  const PROPS = {
    initialData: GRADE_BENEFIT,
    grades: Grades,
    products: Products,
    menus: Menus,
    benefit,
    tabKey,
    isLoading,
    selected: selectedItems,
    categories: Categories,
    shops: Shops,
    isOpened,
    searchInput,
    searchedColumn,

    //
    onChange: handleChange,
    onTabChange: setTabKey,
    onSelect: handleSelect,
    onSubmit: handleSubmit,
    onToggle: handleToggleModal,
    onSearchedColumn: setSearchedColumn,
  };
  /* ===== RENDER ===== */
  return (
    <>
      <GradeBenefitPresenter {...PROPS} />
    </>
  );
};

export default GradeBenefitContainer;
